/**
 * Full-screen knowledge processing console shown while a governance document
 * is validated. Replaces the small "Validating document" dialog.
 */
import React from "react";
import { Loader2 } from "lucide-react";
import { ConsoleShell } from "./ConsoleShell";
import { PipelineOrbit2D } from "./PipelineOrbit2D";
import {
  buildResultTone,
  usePipelineRun,
  type ConsolePhase,
} from "./processingPipeline";
import type { ValidateFileResult } from "../../../api/frameworksApi";

export interface ProcessingConsoleProps {
  fileName?: string;
  fileSize?: string;
  documentTitle?: string;
  documentType?: string;
  /** "running" while the request is in flight, then "complete" or "error". */
  phase: ConsolePhase;
  result?: ValidateFileResult | null;
  errorMessage?: string | null;
  onCancel?: () => void;
  /**
   * Optional back-to-picker action, forwarded to the console shell so a
   * Back button appears in the top bar alongside Cancel.
   */
  onBack?: () => void;
  /** Fires once the closing stages have played out. */
  onFinished?: () => void;
  /** Extra controls for the top bar (used by the preview routes). */
  topBarExtra?: React.ReactNode;
  /** Blocking message shown next to the elapsed time. */
  notice?: { title: string; detail?: string } | null;
  /** Action bar pinned to the bottom of the console. */
  footer?: React.ReactNode;
  /** Send the console to the floating status bubble. */
  onMinimize?: () => void;
  /**
   * Duration the backing request took, once it has returned. Paces the closing
   * stages and the seven-layer sweep.
   */
  apiDurationMs?: number | null;
  /**
   * Render in-flow inside the app layout (sidebar stays visible) rather than
   * as a full-screen overlay. The validation flow uses the overlay; the routed
   * console pages use inline.
   */
  inline?: boolean;
  /** Callback when user clicks Save after/during validation. */
  onSave?: () => void;
  /** True while the Save/Upload request is executing. */
  saving?: boolean;
  /** Label for the save button. Defaults to "Save Document". */
  saveLabel?: string;
  /**
   * Console is mounted but hidden (minimised to the status bubble). The run
   * keeps ticking; only the canvas animation is parked.
   */
  paused?: boolean;
  /** Reports the stage the run is lit on, so the bubble can echo it. */
  onStageChange?: (stage: number) => void;
}

export function ProcessingConsole({
  fileName,
  fileSize,
  documentTitle,
  documentType,
  phase,
  result = null,
  errorMessage = null,
  onCancel,
  onBack,
  onFinished,
  topBarExtra,
  notice,
  footer,
  onMinimize,
  apiDurationMs = null,
  inline,
  onSave,
  saving = false,
  saveLabel = "Save Document",
  paused = false,
  onStageChange,
}: ProcessingConsoleProps) {
  const run = usePipelineRun(
    phase,
    onFinished,
    apiDurationMs ?? result?.durationMs ?? null,
    result?.sevenLayerValidation?.clearedLayers ?? null,
  );

  const onStageChangeRef = React.useRef(onStageChange);
  onStageChangeRef.current = onStageChange;
  React.useEffect(() => {
    onStageChangeRef.current?.(run.stage);
  }, [run.stage]);

  const defaultFooter =
    onSave || onCancel ? (
      <div className="flex w-full items-center justify-end gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
        )}
        {onSave && (
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 disabled:opacity-40 cursor-pointer"
          >
            {saving && <Loader2 className="size-4 animate-spin" />}
            {saving ? "Saving & Uploading…" : saveLabel}
          </button>
        )}
      </div>
    ) : null;

  return (
    <ConsoleShell
      fileName={fileName}
      fileSize={fileSize}
      documentTitle={documentTitle}
      documentType={documentType}
      phase={phase}
      run={run}
      result={result}
      errorMessage={errorMessage}
      onCancel={onCancel}
      onBack={onBack}
      topBarExtra={topBarExtra}
      notice={notice}
      footer={footer !== undefined ? footer : defaultFooter}
      onMinimize={onMinimize}
      inline={inline}
      core={
        <PipelineOrbit2D
          run={run}
          paused={paused}
          settled={!!result}
          tone={buildResultTone(result, phase)}
        />
      }
    />
  );
}
